<script setup lang="tsx">
import type { CharacteristicItem } from '~/components/CharacteristicsGrid';
import type {
	SearchParams,
	SuggestionCondoResult
} from '~/components/SearchSuggest/types';
import type { Tab } from '~/components/TabsNavigator';
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex
} from '~/components/ui/breadcrumb';
import type { SearchSelectionEvent } from '~/composables/useNewSearchSuggest';
import type { Agent } from '~/core/entities/agent/Agent';
import type { PropertyTypeObject } from '~/core/entities/CRMEnums';
import type { Property } from '~/core/entities/property';
import type { LocationClickedEvent, SectionName } from '~/types/mixpanel';
import type { ApplicationError } from '~~/core/helpers/result';

import { useSearchSuggestNavigation } from '@/composables/useSearchSuggestNavigation';
import {
	breakpointsTailwind,
	until,
	useBreakpoints,
	useClipboard
} from '@vueuse/core';
import { floatToLocaleString } from '#imports';
import { useEvents } from '~/composables/events/useEvents';
import { usePropertyPageJsonLd } from '~/composables/jsonLd/usePropertyPageJsonLd';
import { useLogger } from '~/composables/useLogger';
import { searchSuggestBus } from '~/composables/useNewSearchSuggest';
import { providePropertyContext } from '~/composables/usePropertyContext';
import { useSeoTagDetails } from '~/composables/useSeoTagsDetails';
import { markShortsOpenIntent } from '~/composables/useVideoQoE';
import { PropertyPublicationStatus } from '~/core/entities/api/ApiProperty';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { PropertyPresenter } from '~/presenters/PropertyPresenter';
import { hasShortsVideo } from '~/utils';
import { getScrollTopForElement } from '~/utils/fixedHeaderOffset';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt/nuxtPropertiesRepository';
import {
	getAgentFirstName,
	getLocationSlugFromProperty,
	grammarInflection,
	isEmpty
} from '~~/utils';
import { useRoute, useRouter } from 'vue-router';

import { PropertyAvailableContent } from '~/components/Property/PropertyAvailableContent';
import { PropertyUnavailableContent } from '~/components/Property/PropertyUnavailableContent';
import { blessSharedVideoElement } from '~/components/PropertyReelsFeed/useIOSSharedVideo';
import { useToast } from '~/components/ui/toast';

import IconMaterialSymbolsLightBathtubOutline from '~icons/material-symbols-light/bathtub-outline';
import IconMaterialSymbolsLightBedOutlineSharp from '~icons/material-symbols-light/bed-outline-sharp';
import IconMaterialSymbolsLightCorporateFare from '~icons/material-symbols-light/corporate-fare';
import IconMaterialSymbolsLightDirectionsCarOutline from '~icons/material-symbols-light/directions-car-outline';
import MaterialSymbolsLightHistoryEduOutline from '~icons/material-symbols-light/history-edu-outline';
import IconMaterialSymbolsLightOpenInFull from '~icons/material-symbols-light/open-in-full';
import IconMaterialSymbolsLightShowerOutline from '~icons/material-symbols-light/shower-outline';
import IconMaterialSymbolsStraighten from '~icons/material-symbols-light/straighten-outline';

definePageMeta({
	layout: false
});

const {
	trackPageView,
	trackCtaViewMoreClicked,
	trackBreadcrumbClicked,
	trackCtaOffMarketClicked,
	trackFormPropertyClicked,
	trackPropertyCardClicked,
	trackModalPhotoOpened,
	trackModalVideoOpened,
	trackMapPropertyOpened,
	trackCopiedCommercialId,
	trackCtaSimilarPropertiesClicked,
	trackCtaHomeClicked,
	trackPageError
} = useEvents();
const { setPreferences } = usePreferencesCookie();
const logger = useLogger('PropertyPage');
const { toast } = useToast();
const mixpanel = useMixpanel();
const getCookieData = useCookieData();
const { ORIGIN } = useRuntimeConfig().public;
const { copy } = useClipboard();
const { user, loggedIn } = useUserSession();

const {
	handleSelectCode,
	handleSelectCondo,
	handleSelectPlace,
	handleSelectAddress
} = useSearchSuggestNavigation({
	currentPage: 'Property Details'
});

const searchSuggestBusCleanup = onClientEventBus(
	searchSuggestBus,
	(event: SearchSelectionEvent) => {
		switch (event.type) {
			case 'address':
				handleSelectAddress(event.payload);
				break;
			case 'place':
				handleSelectPlace(event.payload);
				break;
			case 'condo':
				handleSelectCondo(event.payload);
				break;
			case 'code':
				handleSelectCode(event.payload);
				break;
		}
	}
);

const breakpoints = useBreakpoints(breakpointsTailwind);

// SSR-safe: default to desktop (true) on server, update on client
const isLgAndUp = ref(true);
onMounted(() => {
	isLgAndUp.value = breakpoints.greaterOrEqual('lg').value;

	watch(
		() => breakpoints.greaterOrEqual('lg').value,
		newValue => {
			isLgAndUp.value = newValue;
		}
	);
});

const router = useRouter();
const route = useRoute();
const { commercialId: routeCommercialId, slug } = useRoute().params as {
	commercialId: string;
	slug: string;
};

onMounted(() => {
	const lastViewedPropertyCookie = useCookie('last-viewed-property', {
		maxAge: 60 * 60 * 24 * 30
	});
	lastViewedPropertyCookie.value = routeCommercialId;
});

useHead({
	meta: [
		{
			property: 'og:url',
			content: `${ORIGIN}/imovel/${routeCommercialId}/${slug}`
		}
	]
});

const pageKey = computed(() => `imovel:${routeCommercialId}`);

// Tracks a failed property fetch so we can tell a genuine "not found" apart
// from a transient failure when deciding the status below.
const propertyFetchError = ref<ApplicationError | null>(null);

const {
	data: property,
	status: propertyStatus,
	error: propertyError,
	refresh: refreshProperty
} = await useAsyncData<Property | null>(
	pageKey.value,
	async () => {
		const [propertyData, error] = await nuxtPropertiesRepository.getOne(
			routeCommercialId,
			user.value?.id,
			getCookieData()
		);

		if (!propertyData || error) {
			propertyFetchError.value = error ?? null;
			return null;
		}

		propertyFetchError.value = null;

		// Set HTTP 410 Gone status for unavailable properties during SSR
		if (
			propertyData.publicationStatus !== PropertyPublicationStatus.PUBLISHED
		) {
			const event = useRequestEvent();
			if (event) {
				setResponseStatus(event, 410, 'Gone');
			}
		}

		return propertyData;
	},
	{
		default: () => null
	}
);

// A transient backend failure must surface a retryable status so search engines
// retry instead of dropping the URL. Only a real 404/400 falls through to the
// "not found" handling below.
if (propertyFetchError.value) {
	const statusCode =
		(propertyFetchError.value.originalErrorObject as { statusCode?: number })
			?.statusCode || 500;

	if (statusCode !== 404 && statusCode !== 400) {
		throw createError({
			statusCode,
			statusMessage:
				propertyFetchError.value.message || 'Erro ao carregar imóvel',
			fatal: true
		});
	}
}

// Handle 404 error when property is not found
if (!property.value && propertyStatus.value !== 'pending') {
	throw createError({
		statusCode: 404,
		statusMessage: 'Imóvel não encontrado',
		message: 'O imóvel que você está procurando não foi encontrado.',
		fatal: true
	});
}

// Computed to check if property is published
const isPropertyPublished = computed<boolean>(() => {
	return (
		property.value?.publicationStatus === PropertyPublicationStatus.PUBLISHED
	);
});

// Determine layout based on property publication status
// Use default-primary layout for unavailable properties (fixed primary header)
// Use property-details layout for available properties (contextual header with animations)
const layoutName = computed(() =>
	isPropertyPublished.value ? 'property-details' : 'default-primary'
);

// i18n for unavailable property
const messagesData = {
	pt: {
		goToHome: 'Ir para Home',
		seeProperties: 'Ver imóveis em {region}',
		unavailablePropertyTitle: 'Este imóvel não está mais disponível',
		unavailablePropertyDescription:
			'Explore outras opções de imóveis semelhantes na mesma região.'
	}
};

const strings = messagesData.pt;

// Set preferences and track page view based on publication status
whenever(
	property,
	async propertyData => {
		if (!propertyData) return;

		// Set preferences for both published and unpublished properties
		setPreferences({
			states: propertyData.state ? [propertyData.state] : [],
			regions: propertyData.region ? [propertyData.region] : [],
			askingPrice: propertyData.askingPrice ?? 0,
			cities: propertyData.city ? [propertyData.city] : [],
			propertyTypes: propertyData.propertyType?.identifier
				? [propertyData.propertyType.identifier]
				: []
		});
	},
	{ immediate: true }
);

onMounted(() => {
	setPreferences({
		states: property.value?.state ? [property.value?.state] : [],
		regions: property.value?.region ? [property.value?.region] : [],
		askingPrice: property.value?.askingPrice ?? 0,
		cities: property.value?.city ? [property.value?.city] : [],
		propertyTypes: property.value?.propertyType.identifier
			? [property.value?.propertyType.identifier]
			: []
	});

	if (property.value) {
		const shouldDeferTracking = isPropertyPublished.value;

		if (!shouldDeferTracking) {
			trackPageView({
				current_page: isPropertyPublished.value
					? 'Detalhe do Imóvel'
					: 'Unpublished Property',
				previous_page_url: isPropertyPublished.value
					? document.referrer
					: document.referrer || `/imovel/${routeCommercialId}`,
				event_name: 'page_viewed',
				[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(property.value)
			});

			mixpanel.trackPageview({
				[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(property.value)
			});
		}
	}

	// Track page view for unpublished properties
	if (!isPropertyPublished.value && property.value) {
		trackPageError({
			[MixpanelProps.ERROR_CODE]: 'unpublished property',
			[MixpanelProps.PAGE_URL]: route.fullPath,
			[MixpanelProps.COMMERCIAL_ID]: routeCommercialId
		});
	}
});

function handleShortsHasVideos(value: boolean): void {
	if (!property.value) return;
	trackPageView({
		current_page: 'Detalhe do Imóvel',
		previous_page_url: document.referrer,
		event_name: 'page_viewed',
		[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(property.value)
	});
	mixpanel.trackPageview({
		[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(property.value),
		[MixpanelProps.HAS_SHORTS_SECTION]: value
	});
}

const isFetchingProperty = computed<boolean>(
	() => propertyStatus.value === 'pending'
);

useSeoTagDetails(property);

// Add noindex meta tag for unavailable properties (410 Gone) to prevent search engines from indexing them
useHead({
	meta: computed(() =>
		!isPropertyPublished.value
			? [
					{
						name: 'robots',
						content: 'noindex, nofollow'
					}
				]
			: []
	)
});

const { data: similarProperties, status: similarPropertiesStatus } =
	useAsyncData<Property[]>(
		`imovel:${routeCommercialId}:similar-properties`,
		async () => {
			await until(property).toBeTruthy();

			if (!property.value) return [];

			const [similarProperties, error] =
				await nuxtPropertiesRepository.getSimilarProperties(
					{
						identifier: routeCommercialId,
						propertyType: [property.value?.propertyType?.identifier],
						region: property.value?.region ?? undefined,
						markUserWishlistsId: user.value?.id
					},
					getCookieData()
				);

			if (!similarProperties || error) return [];

			return similarProperties.properties || [];
		},
		{
			lazy: true
		}
	);

const isFetchingSimilarProperties = computed<boolean>(
	() => similarPropertiesStatus.value === 'pending'
);

const { data: chosenForYouProperties, status: chosenForYouPropertiesStatus } =
	useAsyncData<Property[] | null>(
		`imovel:${routeCommercialId}:chosen-for-you`,
		async () => {
			await until(property).toBeTruthy();

			// O carrossel legado permanece apenas na PDP de imóvel indisponível.
			if (!property.value || isPropertyPublished.value) return null;

			const [chosenForYouProperties, error] =
				await nuxtPropertiesRepository.getChosenForYouProperties(
					{
						identifier: routeCommercialId,
						regions: property.value.region ? [property.value.region] : [],
						askingPrice: property.value.askingPrice ?? undefined,
						markUserWishlistsId: user.value?.id,
						excludeFeaturedVideos: true
					},
					getCookieData()
				);

			if (!chosenForYouProperties || error) return null;

			return chosenForYouProperties;
		},
		{
			lazy: true
		}
	);

const isFetchingChosenForYouProperties = computed<boolean>(
	() => chosenForYouPropertiesStatus.value === 'pending'
);

whenever(
	property,
	async ({ commercialId, slug }) => {
		const canonicalPath = `/imovel/${commercialId}/${slug}`;
		const isOnCanonicalPath = router.currentRoute.value.path === canonicalPath;

		if (!isOnCanonicalPath) await router.replace(canonicalPath);
	},
	{ immediate: true }
);

const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
	if (!property.value) return [];

	return PropertyPresenter.getBreadcrumItems(property.value);
});

const characteristicsKeys = readonly([
	'area',
	'totalArea',
	'bedrooms',
	'parkingSpots',
	'suites',
	'bathrooms'
] as const);

const characteristicHandler = computed<
	Record<
		(typeof characteristicsKeys)[number],
		(value: number) => CharacteristicItem
	>
>(() => ({
	area: (value: number) => {
		const squareMeterPrice = (property.value?.askingPrice || 0) / value;

		const squareMeterPriceCurrency = floatToLocaleString(squareMeterPrice, {
			style: 'currency'
		});

		const title = computed(() => {
			const baseTitle = 'Área útil';
			const pricePerSquareMeter = ` (${squareMeterPriceCurrency}/m²)`;

			return isLgAndUp.value ? `${baseTitle}${pricePerSquareMeter}` : baseTitle;
		});

		return {
			title: title.value,
			value: `${value}m²`,
			icon: IconMaterialSymbolsStraighten
		};
	},
	totalArea: (value: number) => ({
		title: 'Área total',
		value: `${value}m²`,
		icon: IconMaterialSymbolsLightOpenInFull,
		iconClass: 'rotate-90'
	}),
	bedrooms: (value: number) => ({
		value: `${value} ${grammarInflection(value, 'quarto', 'quartos')}`,
		icon: IconMaterialSymbolsLightBedOutlineSharp
	}),
	parkingSpots: (value: number) => ({
		value: `${value} ${grammarInflection(value, 'vaga', 'vagas')}`,
		icon: IconMaterialSymbolsLightDirectionsCarOutline
	}),
	bathrooms: (value: number) => ({
		value: `${value} ${grammarInflection(value, 'banheiro', 'banheiros')}`,
		icon: IconMaterialSymbolsLightShowerOutline
	}),
	suites: (value: number) => ({
		value: `${value} ${grammarInflection(value, 'suíte', 'suítes')}`,
		icon: IconMaterialSymbolsLightBathtubOutline
	})
}));

const characteristics = computed<CharacteristicItem[]>(() => {
	const basicCharacteristics = characteristicsKeys
		.map(key => {
			const value = property.value?.[key] ?? 0;
			if (value <= 0) return null;

			return characteristicHandler.value[key](value);
		})
		.filter(Boolean) as CharacteristicItem[];

	const completeCharacteristics: CharacteristicItem[] = [];

	if (property.value?.launchDate) {
		completeCharacteristics.push({
			title: 'Ano de construção',
			value: new Date(property.value.launchDate).getFullYear().toString(),
			icon: MaterialSymbolsLightHistoryEduOutline
		});
	}

	if (
		property.value?.canonicalCondo?.name &&
		property.value?.canonicalCondo?.slug
	) {
		completeCharacteristics.push({
			value: property.value.canonicalCondo.name,
			icon: IconMaterialSymbolsLightCorporateFare,
			isLink: true,
			linkTo: `/condominio/${property.value.canonicalCondo.slug}`,
			class: 'hidden md:flex'
		});
	}

	return [...basicCharacteristics, ...completeCharacteristics];
});

const hasFeaturesByCategory = computed<boolean>(
	() => !isEmpty(property.value?.featuresByCategory ?? {})
);

const copyCommercialId = async () => {
	if (import.meta.client) await copy(property.value?.commercialId ?? '');
	trackCopiedCommercialId({
		[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId ?? ''
	});
};

const hasImagesEnoughToShowHighlightedImagesSections = computed<boolean>(
	() =>
		!isEmpty(property.value?.images ?? []) &&
		(property.value?.images.length ?? 0) >= 9
);

const shouldShowCharacteristics = computed<boolean>(
	() =>
		hasFeaturesByCategory.value ||
		hasImagesEnoughToShowHighlightedImagesSections.value ||
		isFetchingProperty.value
);

const shouldShowFeaturesSection = computed<boolean>(
	() => hasFeaturesByCategory.value || isFetchingProperty.value
);

const shouldShowHighlightedImagesSection = computed<boolean>(
	() =>
		hasImagesEnoughToShowHighlightedImagesSections.value ||
		isFetchingProperty.value
);

const launchYear = computed<string>(() => {
	const date = property.value?.launchDate;

	if (!date) return '';

	return new Date(date).getFullYear().toString();
});

const agentFirstName = computed<string>(() =>
	getAgentFirstName(property.value?.agent)
);
const heroCtaText = computed(() => `Falar com ${agentFirstName.value}`);

const numberOfImages = computed<number>(
	() => property.value?.images.length ?? 0
);

const similarPropertiesHref = computed<string | null>(() => {
	if (!property.value) return null;

	return PropertyFiltersPresenter.getPathFromFilters({
		cities: [property.value.city],
		states: [property.value.state],
		regions: property.value.region ? [property.value.region] : [],
		propertyTypes: [property.value.propertyType?.identifier].filter(Boolean),
		maxAskingPrice: property.value.askingPrice || undefined
	});
});

const chosenForYouCtaLink = computed<string>(() =>
	PropertyFiltersPresenter.getPathFromFilters({
		regions: [property.value?.region ?? ''],
		states: [property.value?.state ?? ''],
		cities: [property.value?.city ?? ''],
		maxAskingPrice: property.value?.askingPrice
			? Math.ceil(property.value.askingPrice * 1.15)
			: undefined,
		isChosenForYou: true
	})
);

// Unavailable property computed values
const propertyRegion = computed(() => property.value?.region || '');
const unavailablePropertiesUrl = computed(() => {
	return PropertyFiltersPresenter.getPathFromFilters({
		regions: [propertyRegion.value],
		cities: [property.value?.city || ''],
		states: [property.value?.state || '']
	});
});

const centerCoordinates = computed<{ lat: number; lng: number }>(() => ({
	lat: property.value?.h3Cluster?.geometry?.coordinates[0] ?? 0,
	lng: property.value?.h3Cluster?.geometry?.coordinates[1] ?? 0
}));

const isContactModalOpen = shallowRef(false);

const shouldShowFixedFooterExclusiveTag = ref(false);
const shouldShowStickyPulse = ref(false);

provide('shouldShowStickyPulse', shouldShowStickyPulse);

const { scrollDirection } = useScrollDirection();

const shouldShowMobileFixedFooterExclusiveTag = computed<boolean>(
	() =>
		!!(
			property.value?.isExclusive &&
			shouldShowFixedFooterExclusiveTag.value &&
			scrollDirection.value === 'down'
		)
);

const openContactModal = () => {
	// INP optimization: defer modal rendering to next frame
	requestAnimationFrame(() => {
		isContactModalOpen.value = true;
	});
};

const offMarketBus = onClientEventBus(
	useEventBus<void>('cta_off_market_clicked'),
	() => {
		const currentPage = isPropertyPublished.value
			? 'Property Details'
			: 'Unpublished Property';

		trackCtaOffMarketClicked({
			[MixpanelProps.CTA_TEXT]: 'Imóveis Off-Market',
			[MixpanelProps.CURRENT_PAGE]: currentPage,
			[MixpanelProps.SECTION_NAME]: 'selected properties',
			[MixpanelProps.DESTINATION_URL]: '/off-market'
		});
	}
);

function handleBreadcrumbClicked(event: BreadcrumbItemWithIndex) {
	trackBreadcrumbClicked({
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.CTA_TEXT]: event.label,
		[MixpanelProps.DESTINATION_URL]: event.route as unknown as string,
		[MixpanelProps.CLICK_HOME]: event.index === 0,
		[MixpanelProps.CLICK_CITY]: event.index === 1,
		[MixpanelProps.CLICK_NEIGHBORHOOD]: event.index === 2
	});
}

function handleViewMoreClicked(
	sectionName: SectionName,
	destination: string,
	ctaText: string
) {
	const currentPage = isPropertyPublished.value
		? 'Detalhe do Imóvel'
		: 'Unpublished Property';

	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: currentPage,
		[MixpanelProps.DESTINATION_URL]: destination,
		[MixpanelProps.CTA_TEXT]: ctaText || 'Ver todos',
		[MixpanelProps.SECTION_NAME]: sectionName
	});
}

// Handler for unavailable property view more clicked
function handleUnavailableViewMoreClicked(params: {
	destination: string;
	clickCount: number;
	ctaText: string;
	sectionName: SectionName;
}) {
	trackCtaViewMoreClicked({
		cta_text: params.ctaText,
		destination_url: params.destination,
		click_count: params.clickCount,
		[MixpanelProps.SECTION_NAME]: params.sectionName,
		[MixpanelProps.CURRENT_PAGE]: 'Unpublished Property'
	});
}

// Handler for unavailable property go to home clicked
function handleGoToHomeClicked() {
	trackCtaHomeClicked({
		cta_text: strings.goToHome,
		destination_url: '/',
		[MixpanelProps.CURRENT_PAGE]: 'Unpublished Property'
	});
}

function handleFormPropertyClicked(
	sectionName: SectionName,
	ctaText: string,
	companyName: string,
	agentName: string,
	askingPrice: number | null,
	openModal: boolean = false
) {
	if (!property.value) {
		return;
	}

	if (openModal) openContactModal();

	trackFormPropertyClicked({
		[MixpanelProps.CTA_TEXT]: ctaText,
		[MixpanelProps.COMPANY_NAME]: companyName,
		[MixpanelProps.AGENT_NAME]: agentName,
		[MixpanelProps.COMMERCIAL_ID]: property.value.commercialId,
		[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Imóvel',
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.USER_STATUS]: loggedIn.value ? 'logged in' : 'logged out'
	});
}

function handleNearPropertiesClicked(destinationUrl: string) {
	if (!property.value) {
		console.warn(
			'Attempting to handle near properties click without property data'
		);
		return;
	}

	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Imóvel',
		[MixpanelProps.SECTION_NAME]: 'property location',
		[MixpanelProps.COMMERCIAL_ID]: property.value.commercialId,
		[MixpanelProps.DESTINATION_URL]: destinationUrl,
		[MixpanelProps.CTA_TEXT]: 'Imóveis próximos'
	});
}

const nearPropertiesLink = computed<string>(() => {
	if (!property.value) return '';

	return PropertyFiltersPresenter.getPathFromFilters({
		regions: property.value.region ? [property.value.region] : [],
		states: [property.value.state],
		cities: [property.value.city]
	});
});

usePropertyPageJsonLd(() => property.value || null);

function toggleContactModal() {
	// INP optimization: defer modal state change to next frame
	requestAnimationFrame(() => {
		if (isContactModalOpen.value !== undefined) {
			isContactModalOpen.value = !isContactModalOpen.value;
		}
	});
}

function handleHeroFormPropertyClicked(params: {
	sectionName: string;
	ctaText: string;
	companyName: string;
	agentName: string;
	askingPrice: number | null;
	openModal: boolean;
}) {
	handleFormPropertyClicked(
		params.sectionName as SectionName,
		params.ctaText,
		params.companyName,
		params.agentName,
		params.askingPrice,
		params.openModal
	);
}

const isPropertyModalOpen = ref(false);
const currentPropertyModalTab = ref<Tab>('images');
const initialPhotoIndex = ref<number>(0);
const isCarouselOpen = ref<boolean>(false);

const HERO_SHORTS_SECTION_NAME: SectionName = 'similar shorts';
const HERO_SHORTS_CARD_TYPE = 'shorts card' as const;
const HERO_SHORTS_CARD_POSITION = 0;

const openPropertyModal = (tab: Tab) => {
	// INP optimization: defer heavy modal rendering to next frame
	requestAnimationFrame(() => {
		currentPropertyModalTab.value = tab;
		isPropertyModalOpen.value = true;
	});
};

function handlePhotoModalOpened(
	sectionName: SectionName,
	clickType: 'photo' | 'cta',
	photoIndex: number,
	photoUrl?: string,
	ctaText?: string
) {
	initialPhotoIndex.value = photoIndex;

	openPropertyModal('images');

	trackModalPhotoOpened({
		[MixpanelProps.CTA_TEXT]: ctaText,
		[MixpanelProps.CLICK_PHOTO]: clickType === 'photo',
		[MixpanelProps.CLICK_CTA]: clickType === 'cta',
		[MixpanelProps.PHOTO_URL]: photoUrl,
		[MixpanelProps.PHOTO_COUNT]: photoIndex + 1,
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!
	});
}

function handlePhotoClicked(photoIndex: number) {
	isCarouselOpen.value = true;
	initialPhotoIndex.value = photoIndex;

	openPropertyModal('images');

	trackModalPhotoOpened({
		[MixpanelProps.CLICK_PHOTO]: true,
		[MixpanelProps.CLICK_CTA]: false,
		[MixpanelProps.PHOTO_COUNT]: numberOfImages.value,
		[MixpanelProps.PHOTO_URL]: property.value?.images[photoIndex]?.url,
		[MixpanelProps.PHOTO_POSITION]: photoIndex,
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.SECTION_NAME]: 'hero section',
		[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!
	});
}

function handlePropertyVideoModalOpened(
	clickType: 'video' | 'cta',
	videoUrl?: string
) {
	if (clickType === 'video') {
		if (!property.value?.commercialId) {
			logger.error('Missing property commercialId when opening hero short', {
				clickType,
				videoUrl,
				route: route.fullPath
			});

			toast({
				title: 'Não foi possível abrir o vídeo',
				description: 'Tente novamente em instantes.',
				variant: 'destructive'
			});

			return;
		}

		trackPropertyCardClicked({
			[MixpanelProps.COMMERCIAL_ID]: property.value.commercialId,
			[MixpanelProps.DESTINATION_URL]: `/shorts/${property.value.commercialId}`,
			[MixpanelProps.CURRENT_PAGE]: route.fullPath,
			[MixpanelProps.SECTION_NAME]: HERO_SHORTS_SECTION_NAME,
			[MixpanelProps.CARD_POSITION]: HERO_SHORTS_CARD_POSITION,
			[MixpanelProps.CARD_TYPE]: HERO_SHORTS_CARD_TYPE,
			[MixpanelProps.TAG_EXCLUSIVE]: property.value.isExclusive ?? false,
			[MixpanelProps.COMPANY_NAME]: property.value.company?.name ?? '',
			[MixpanelProps.AGENT_NAME]: property.value.agent?.name ?? ''
		});

		markShortsOpenIntent();
		blessSharedVideoElement();
		navigateTo(`/shorts/${property.value.commercialId}`);
		return;
	}

	openPropertyModal('videos');
	trackVideoModalOpenedFromCta(videoUrl);
}

function trackVideoModalOpenedFromCta(videoUrl?: string) {
	trackModalVideoOpened({
		[MixpanelProps.CLICK_VIDEO]: false,
		[MixpanelProps.CLICK_CTA]: true,
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
		[MixpanelProps.VIDEO_URL]: videoUrl
	});
}

function handlePropertyModalVideoClicked(videoUrl: string) {
	trackModalVideoOpened({
		[MixpanelProps.CLICK_VIDEO]: true,
		[MixpanelProps.CLICK_CTA]: false,
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
		[MixpanelProps.VIDEO_URL]: videoUrl
	});
}

function handleMapModalOpened(
	sectionName: SectionName,
	ctaText: string,
	clickEvent?: LocationClickedEvent
) {
	openPropertyModal('map');

	const allowedSectionToClickLinkAndMap = ['overview'];
	const clickLinkProperty = allowedSectionToClickLinkAndMap.includes(
		sectionName
	)
		? clickEvent?.clickLink
		: undefined;

	const clickMapProperty = allowedSectionToClickLinkAndMap.includes(sectionName)
		? clickEvent?.clickMap
		: undefined;

	trackMapPropertyOpened({
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.CTA_TEXT]: ctaText,
		[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
		[MixpanelProps.CLICK_LINK]: clickLinkProperty,
		[MixpanelProps.CLICK_MAP]: clickMapProperty
	});
}

function scrollToSection(ref: HTMLElement | { $el: HTMLElement } | null) {
	if (ref) {
		const element = ref instanceof HTMLElement ? ref : ref.$el;

		scrollTo({
			top: getScrollTopForElement(element, 12),
			behavior: 'smooth'
		});
	}
}

const shouldShowSimilarPropertyCTA = computed<boolean>(
	() => (similarProperties.value?.length ?? 0) > 0
);

// Template ref for PropertyAvailableContent to access its exposed refs
const propertyAvailableContentRef = ref<{
	highlightedRegionPropertiesDetailsSectionRef: HTMLElement | null;
	floatingCtaChosenForYouRef: HTMLElement | null;
	similarPropertiesCtaTargetRef: HTMLElement | null;
} | null>(null);

function scrollToSimilarProperties(ctaType: 'floating' | 'fixed' = 'fixed') {
	if (!property.value) {
		return;
	}

	if (ctaType === 'fixed') {
		const element =
			propertyAvailableContentRef.value?.similarPropertiesCtaTargetRef ??
			propertyAvailableContentRef.value?.floatingCtaChosenForYouRef ??
			propertyAvailableContentRef.value
				?.highlightedRegionPropertiesDetailsSectionRef;

		if (element) {
			scrollToSection(element);
		}
	}

	trackCtaSimilarPropertiesClicked({
		[MixpanelProps.COMMERCIAL_ID]: property.value.commercialId,
		[MixpanelProps.CLICK_CTA_FLOATING]: ctaType === 'floating',
		[MixpanelProps.CLICK_CTA_FIXED]: ctaType === 'fixed',
		[MixpanelProps.CTA_TEXT]: 'Imóveis similares',
		[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Imóvel'
	});
}

function handleSimilarPropertiesClicked() {
	if (!property.value) {
		return;
	}

	openPropertyModal('similar');

	trackCtaSimilarPropertiesClicked({
		[MixpanelProps.COMMERCIAL_ID]: property.value.commercialId,
		[MixpanelProps.CLICK_CTA_FIXED]: true,
		[MixpanelProps.CLICK_CTA_FLOATING]: false,
		[MixpanelProps.CTA_TEXT]: 'Semelhantes',
		[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Imóvel'
	});
}

const regionClicked = computed<string>(() => {
	return `${property.value?.region}, ${property.value?.city} - ${property.value?.state}`;
});

function scrollToCharacteristics() {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: 'Property Details',
		[MixpanelProps.SECTION_NAME]: 'overview',
		[MixpanelProps.CTA_TEXT]: 'Ver todas as características'
	});
}

providePropertyContext({
	property,
	isLoading: isFetchingProperty,
	isContactModalOpen
});

onUnmounted(() => {
	// Cleanup event listeners
	offMarketBus();
	searchSuggestBusCleanup();
});
</script>

<template>
	<NuxtLayout :name="layoutName">
		<!-- Available Property Content -->
		<PropertyAvailableContent
			v-if="isPropertyPublished"
			ref="propertyAvailableContentRef"
			:property="property"
			:is-fetching-property="isFetchingProperty"
			:is-fetching-similar-properties="isFetchingSimilarProperties"
			:similar-properties="similarProperties"
			:launch-year="launchYear"
			:characteristics="characteristics"
			:breadcrumb-items="breadcrumbItems"
			:should-show-characteristics="shouldShowCharacteristics"
			:should-show-features-section="shouldShowFeaturesSection"
			:should-show-highlighted-images-section="
				shouldShowHighlightedImagesSection
			"
			:similar-properties-href="similarPropertiesHref"
			:center-coordinates="centerCoordinates"
			:near-properties-link="nearPropertiesLink"
			:should-show-fixed-footer-exclusive-tag="
				shouldShowFixedFooterExclusiveTag
			"
			:should-show-mobile-fixed-footer-exclusive-tag="
				shouldShowMobileFixedFooterExclusiveTag
			"
			:should-show-similar-property-c-t-a="shouldShowSimilarPropertyCTA"
			:hero-cta-text="heroCtaText"
			:is-property-modal-open="isPropertyModalOpen"
			:is-contact-modal-open="isContactModalOpen"
			:current-property-modal-tab="currentPropertyModalTab"
			:is-carousel-open="isCarouselOpen"
			:initial-photo-index="initialPhotoIndex"
			@photo-clicked="handlePhotoClicked"
			@photo-modal-opened="handlePhotoModalOpened"
			@video-modal-opened="handlePropertyVideoModalOpened"
			@click-video="handlePropertyModalVideoClicked"
			@similar-clicked="handleSimilarPropertiesClicked"
			@map-modal-opened="handleMapModalOpened"
			@form-property-clicked="
				(params: {
					sectionName: string;
					ctaText: string;
					companyName: string;
					agentName: string;
					askingPrice: number | null;
					openModal?: boolean;
				}) =>
					handleFormPropertyClicked(
						params.sectionName as SectionName,
						params.ctaText,
						params.companyName,
						params.agentName,
						params.askingPrice,
						params.openModal
					)
			"
			@breadcrumb-clicked="handleBreadcrumbClicked"
			@view-more-clicked="handleViewMoreClicked"
			@near-properties-clicked="handleNearPropertiesClicked"
			@scroll-to-characteristics="scrollToCharacteristics"
			@copy-commercial-id="copyCommercialId"
			@toggle-contact-modal="toggleContactModal"
			@scroll-to-similar-properties="scrollToSimilarProperties"
			@open-contact-modal="openContactModal"
			@update:should-show-fixed-footer-exclusive-tag="
				(value: boolean) => (shouldShowFixedFooterExclusiveTag = value)
			"
			@update:should-show-sticky-pulse="
				(value: boolean) => (shouldShowStickyPulse = value)
			"
			@update:is-property-modal-open="
				(value: boolean) => (isPropertyModalOpen = value)
			"
			@update:current-property-modal-tab="
				(value: Tab) => (currentPropertyModalTab = value)
			"
			@update:is-carousel-open="(value: boolean) => (isCarouselOpen = value)"
			@update:is-contact-modal-open="
				(value: boolean) => (isContactModalOpen = value)
			"
			@update:has-shorts-videos="handleShortsHasVideos"
		/>

		<!-- Unavailable Property Content -->
		<PropertyUnavailableContent
			v-else
			:property="property"
			:similar-properties="similarProperties"
			:chosen-for-you-properties="chosenForYouProperties"
			:is-loading-selected-properties="isFetchingChosenForYouProperties"
			:property-region="propertyRegion"
			:properties-url="unavailablePropertiesUrl"
			:chosen-for-you-cta-link="chosenForYouCtaLink"
			@view-more-clicked="handleUnavailableViewMoreClicked"
			@go-to-home-clicked="handleGoToHomeClicked"
		/>
	</NuxtLayout>
</template>
